/**
 * CUSTOMER-R4.9 / K20 — primary bank-pay CTA + transient launch bridge.
 * Auto-attempt is allowed only after STG exists (checkout pay-now).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { tryOpenNbuBankApp, type BankPaymentClientPayload } from "./bank-iban-payment-v2.js";
import { bankPaymentIntentFromClient } from "./bank-payment-intent-v2.js";
import {
  markBankBridgeRendered,
  markBankDeeplinkDispatched,
  markBankLauncherOpenClicked,
  markPageBecameHidden,
  markPageReturnedVisible,
} from "./bank-launcher-telemetry-v2.js";
import { PaymentLaunchBridgeV1 } from "./PaymentLaunchBridgeV1.js";
import { IbanPaymentQrV2 } from "./IbanPaymentQrV2.js";

function dispatchNbuUri(uri: string): boolean {
  const opened = tryOpenNbuBankApp(uri);
  if (opened) return true;
  try {
    const popup = window.open(uri, "_blank", "noopener,noreferrer");
    if (popup) return true;
    const a = document.createElement("a");
    a.href = uri;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch {
    return false;
  }
}

export function BankPayActionsV2({
  lang,
  payload,
  testIdPrefix = "sv2-bank-panel",
  onBackToOrder,
  paymentConfirmed,
  autoAttemptHandoff = false,
}: {
  lang: StoreV2Lang;
  payload: BankPaymentClientPayload | null;
  testIdPrefix?: string;
  onBackToOrder?: () => void;
  paymentConfirmed?: boolean;
  /** K20 — one automatic bank-app open after STG, never before order create. */
  autoAttemptHandoff?: boolean;
}) {
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [stalled, setStalled] = useState(false);
  const autoOnce = useRef(false);

  useEffect(() => {
    if (!bridgeOpen) return;
    const closeBridge = () => {
      markPageReturnedVisible();
      setBridgeOpen(false);
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") markPageBecameHidden();
      else closeBridge();
    };
    const onPageShow = () => closeBridge();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pagehide", markPageBecameHidden);
    window.addEventListener("focus", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pagehide", markPageBecameHidden);
      window.removeEventListener("focus", onPageShow);
    };
  }, [bridgeOpen]);

  const intent = payload ? bankPaymentIntentFromClient(payload) : null;

  const dispatchFromIntent = useCallback(() => {
    if (!intent?.nbu_uri) return;
    flushSync(() => {
      setBridgeOpen(true);
      setStalled(false);
    });
    markBankBridgeRendered();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const ok = dispatchNbuUri(intent.nbu_uri as string);
        markBankDeeplinkDispatched(ok);
      });
    });
    window.setTimeout(() => setStalled(true), 1800);
  }, [intent]);

  useEffect(() => {
    if (!autoAttemptHandoff || autoOnce.current || !intent?.nbu_uri || !payload) return;
    autoOnce.current = true;
    markBankLauncherOpenClicked(payload.public_order_number);
    if (document.visibilityState === "hidden") {
      flushSync(() => {
        setBridgeOpen(true);
        setStalled(false);
      });
      return;
    }
    dispatchFromIntent();
  }, [autoAttemptHandoff, dispatchFromIntent, intent, payload]);

  const onPrimary = () => {
    if (!payload || !intent?.nbu_uri) return;
    markBankLauncherOpenClicked(payload.public_order_number);
    dispatchFromIntent();
  };

  if (!payload || !intent) return null;

  return (
    <div className="sv2-bank-pay-actions" data-testid={`${testIdPrefix}-pay-actions`}>
      <button
        type="button"
        className="sv2-cta sv2-primary-commerce"
        data-testid={`${testIdPrefix}-retry-deeplink`}
        onClick={onPrimary}
      >
        {t(lang, stalled ? "bank_open_app" : "bank_pay_via_bank")}
      </button>
      <IbanPaymentQrV2 lang={lang} publicUrl={payload.public_url} />
      <PaymentLaunchBridgeV1
        lang={lang}
        open={bridgeOpen}
        intent={intent}
        stalled={stalled}
        {...(paymentConfirmed !== undefined ? { paymentConfirmed } : {})}
        onRetry={dispatchFromIntent}
        onBackToOrder={
          onBackToOrder
            ? () => {
                setBridgeOpen(false);
                onBackToOrder();
              }
            : () => setBridgeOpen(false)
        }
      />
    </div>
  );
}
